/**
 * tw-variant-selector.js
 * 
 * Shared variant selection utilities for Tailwind PDP components.
 * Handles form updates, URL changes, and partial section updates.
 * Uses same approach as product-variant-picker-visual.liquid for third-party app compatibility.
 */

window.TwVariantSelector = (function() {
  'use strict';

  /**
   * Updates the product form's variant input
   * @param {string} sectionId - The section ID
   * @param {string} variantId - The new variant ID
   */
  function updateFormVariant(sectionId, variantId) {
    const productForms = document.querySelectorAll(
      `#product-form-${sectionId}, #product-form-installment-${sectionId}, form[action*="/cart/add"]`
    );
    
    productForms.forEach(form => {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /**
   * Updates the browser URL with the variant parameter
   * @param {string} variantId - The new variant ID
   */
  function updateUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Fetches new section HTML and performs PARTIAL updates
   * Only updates specific elements to preserve third-party app elements (Qikify, etc.)
   * This matches the approach in product-variant-picker-visual.liquid
   * @param {string} sectionId - The section ID
   * @param {string} productUrl - The product URL
   * @param {string} variantId - The new variant ID
   * @returns {Promise} - Resolves when updates are complete
   */
  function refreshSection(sectionId, productUrl, variantId) {
    // DEBUG: Log refresh start - REMOVE AFTER TESTING
    console.log('=== Section Refresh START ===');
    
    // Preserve the view parameter for alternate gallery support
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view');
    let fetchUrl = `${productUrl}?variant=${variantId}&section_id=${sectionId}`;
    if (viewParam) {
      fetchUrl += `&view=${viewParam}`;
    }
    
    console.log('Fetching:', fetchUrl);
    
    return fetch(fetchUrl)
      .then(response => {
        console.log('Fetch response status:', response.status);
        return response.text();
      })
      .then(html => {
        console.log('HTML received, length:', html.length);
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Update price
        const newPrice = doc.querySelector('.price');
        const currentPrice = document.querySelector(`#shopify-section-${sectionId} .price`);
        if (newPrice && currentPrice) {
          currentPrice.innerHTML = newPrice.innerHTML;
          currentPrice.className = newPrice.className;
        }
        
        // Update product media/gallery
        const newMedia = doc.querySelector('.product__media-list, .product__media-wrapper');
        const currentMedia = document.querySelector(`#shopify-section-${sectionId} .product__media-list, #shopify-section-${sectionId} .product__media-wrapper`);
        if (newMedia && currentMedia) {
          currentMedia.innerHTML = newMedia.innerHTML;
        }
        
        // Update buy button state
        const newBuyButton = doc.querySelector('.product-form__submit');
        const currentBuyButton = document.querySelector(`#shopify-section-${sectionId} .product-form__submit`);
        if (newBuyButton && currentBuyButton) {
          currentBuyButton.disabled = newBuyButton.disabled;
          currentBuyButton.innerHTML = newBuyButton.innerHTML;
        }
        
        // Update our Tailwind scent selector
        const newScentSelector = doc.querySelector('[id^="tw-scent-selector-"]');
        const currentScentSelector = document.querySelector(`#shopify-section-${sectionId} [id^="tw-scent-selector-"]`);
        if (newScentSelector && currentScentSelector) {
          currentScentSelector.innerHTML = newScentSelector.innerHTML;
        }
        
        // Update our Tailwind quantity selector
        const newQtySelector = doc.querySelector('[id^="tw-qty-selector-"]');
        const currentQtySelector = document.querySelector(`#shopify-section-${sectionId} [id^="tw-qty-selector-"]`);
        if (newQtySelector && currentQtySelector) {
          currentQtySelector.innerHTML = newQtySelector.innerHTML;
        }
        
        // Update our Tailwind usage text
        const newUsageText = doc.querySelector('.tw-usage-text-wrapper');
        const currentUsageText = document.querySelector(`#shopify-section-${sectionId} .tw-usage-text-wrapper`);
        if (newUsageText && currentUsageText) {
          currentUsageText.outerHTML = newUsageText.outerHTML;
        } else if (newUsageText && !currentUsageText) {
          // Usage text appeared for new variant - find insertion point
          const qtySelector = document.querySelector(`#shopify-section-${sectionId} [id^="tw-qty-selector-"]`);
          if (qtySelector) {
            qtySelector.insertAdjacentHTML('beforebegin', newUsageText.outerHTML);
          }
        } else if (!newUsageText && currentUsageText) {
          // Usage text no longer exists for new variant - remove it
          currentUsageText.remove();
        }
        
        // Update our Tailwind CTA button (price and state)
        const newCtaButton = doc.querySelector('.tw-cta-button');
        const currentCtaButton = document.querySelector(`#shopify-section-${sectionId} .tw-cta-button`);
        if (newCtaButton && currentCtaButton) {
          // Update button disabled state
          currentCtaButton.disabled = newCtaButton.disabled;
          
          // Update button text (Add to Cart / Sold Out)
          const newText = newCtaButton.querySelector('.tw-cta-text');
          const currentText = currentCtaButton.querySelector('.tw-cta-text');
          if (newText && currentText) {
            currentText.innerHTML = newText.innerHTML;
          }
          
          // Update price badge
          const newPrice = newCtaButton.querySelector('.tw-cta-price');
          const currentPrice = currentCtaButton.querySelector('.tw-cta-price');
          if (newPrice && currentPrice) {
            currentPrice.innerHTML = newPrice.innerHTML;
          }
        }
        
        // Update our Tailwind CTA form inputs (variant ID and selling plan)
        const newCtaForm = doc.querySelector('.tw-pdp-form');
        const currentCtaForm = document.querySelector(`#shopify-section-${sectionId} .tw-pdp-form`);
        
        // DEBUG: Log form detection - REMOVE AFTER TESTING
        console.log('newCtaForm found:', !!newCtaForm);
        console.log('currentCtaForm found:', !!currentCtaForm);
        
        if (newCtaForm && currentCtaForm) {
          // Update variant ID
          const newVariantInput = newCtaForm.querySelector('input[name="id"]');
          const currentVariantInput = currentCtaForm.querySelector('input[name="id"]');
          if (newVariantInput && currentVariantInput) {
            currentVariantInput.value = newVariantInput.value;
          }
          
          // Update selling plan ID (for subscriptions)
          const newSellingPlanInput = newCtaForm.querySelector('input[name="selling_plan"]');
          const currentSellingPlanInput = currentCtaForm.querySelector('input[name="selling_plan"]');
          if (newSellingPlanInput && currentSellingPlanInput) {
            currentSellingPlanInput.value = newSellingPlanInput.value;
          }
          
          // DEBUG: Log form updates - REMOVE AFTER TESTING
          console.log('=== Section Refresh Form Update Debug ===');
          console.log('New Variant ID from server:', newVariantInput?.value);
          console.log('New Selling Plan from server:', newSellingPlanInput?.value);
          console.log('Selling Plan is empty:', !newSellingPlanInput?.value);
        }
        
        // Update our Tailwind purchase type selector (subscription vs one-time)
        const newPurchaseType = doc.querySelector('.tw-purchase-type-wrapper');
        const currentPurchaseType = document.querySelector(`#shopify-section-${sectionId} .tw-purchase-type-wrapper`);
        if (newPurchaseType && currentPurchaseType) {
          // Replace entire component to get new prices/selling plan data
          currentPurchaseType.outerHTML = newPurchaseType.outerHTML;
        }
        
        // Ensure form variant is correct after DOM updates
        updateFormVariant(sectionId, variantId);
        
        // Re-apply international restrictions after DOM updates
        // (hides subscription options for non-US customers)
        if (typeof window.applyInternationalRestrictions === 'function') {
          setTimeout(window.applyInternationalRestrictions, 10);
        }
        
        return html;
      })
      .catch(err => {
        // DEBUG: Enhanced error logging - REMOVE AFTER TESTING
        console.error('=== Section Refresh ERROR ===');
        console.error('Error:', err);
        console.error('Error message:', err.message);
        console.error('Error stack:', err.stack);
        throw err;
      });
  }

  /**
   * Updates visual state for selector cards
   * @param {HTMLElement} container - The selector container
   * @param {HTMLElement} selectedCard - The newly selected card
   * @param {string} cardClass - The card class name (e.g., 'tw-scent-card', 'tw-qty-card')
   * @param {string} checkClass - The check class name (e.g., 'tw-scent-check', 'tw-qty-check')
   */
  function updateCardStates(container, selectedCard, cardClass, checkClass) {
    // Deselect all cards
    container.querySelectorAll(`.${cardClass}`).forEach(card => {
      card.classList.remove('tw-border-brand-primary', 'tw-bg-[#c8effe]');
      card.classList.add('tw-border-gray-200', 'tw-bg-white');
      const check = card.querySelector(`.${checkClass}`);
      if (check) {
        check.classList.add('tw-hidden');
        check.classList.remove('tw-flex');
      }
    });
    
    // Select the clicked card
    selectedCard.classList.remove('tw-border-gray-200', 'tw-bg-white');
    selectedCard.classList.add('tw-border-brand-primary', 'tw-bg-[#c8effe]');
    const check = selectedCard.querySelector(`.${checkClass}`);
    if (check) {
      check.classList.remove('tw-hidden');
      check.classList.add('tw-flex');
    }
  }

  /**
   * Full variant selection handler
   * @param {Object} options - Configuration options
   * @param {string} options.sectionId - The section ID
   * @param {string} options.productUrl - The product URL  
   * @param {string} options.variantId - The new variant ID
   * @param {HTMLElement} options.container - The selector container
   * @param {HTMLElement} options.selectedCard - The newly selected card
   * @param {string} options.cardClass - The card class name
   * @param {string} options.checkClass - The check class name
   */
  function selectVariant(options) {
    const { sectionId, productUrl, variantId, container, selectedCard, cardClass, checkClass } = options;
    
    // 1. Update visual state immediately for responsiveness
    updateCardStates(container, selectedCard, cardClass, checkClass);
    
    // 2. Update form variant
    updateFormVariant(sectionId, variantId);
    
    // 3. Update URL
    updateUrl(variantId);
    
    // 4. Partial refresh (updates only specific elements, preserves third-party app elements)
    return refreshSection(sectionId, productUrl, variantId);
  }

  // Public API
  return {
    updateFormVariant,
    updateUrl,
    refreshSection,
    updateCardStates,
    selectVariant
  };
})();
